/**
 * PortalFraudDetection — Real-time fraud monitoring dashboard
 *
 * Shows fraud scores, flagged sessions, blocklist management,
 * and fraud rule configuration.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ShieldAlert, AlertTriangle, Ban, Loader2, Plus, Trash2, Eye,
  ToggleLeft, ToggleRight, RefreshCw,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { useRealtimeScoring } from '@/hooks/useRealtimeScoring';

interface FraudScore {
  id: string;
  session_id: string;
  fraud_score: number;
  risk_level: string;
  signals: Record<string, unknown>;
  ip_address: string | null;
  action_taken: string;
  reviewed: boolean;
  created_at: string;
}

interface FraudRule {
  id: string;
  rule_name: string;
  description: string | null;
  condition: { field: string; operator: string; value: unknown };
  points: number;
  is_active: boolean;
  priority: number;
}

interface BlocklistEntry {
  id: string;
  block_type: string;
  block_value: string;
  reason: string | null;
  expires_at: string | null;
  created_at: string;
}

type ActiveTab = 'dashboard' | 'rules' | 'blocklist';

const RISK_COLORS: Record<string, string> = {
  low: 'text-emerald-400 bg-emerald-500/20',
  medium: 'text-amber-400 bg-amber-500/20',
  high: 'text-red-400 bg-red-500/20',
  blocked: 'text-red-500 bg-red-500/30',
};

export default function PortalFraudDetection() {
  const [tab, setTab] = useState<ActiveTab>('dashboard');
  const [scores, setScores] = useState<FraudScore[]>([]);
  const [rules, setRules] = useState<FraudRule[]>([]);
  const [blocklist, setBlocklist] = useState<BlocklistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Blocklist form
  const [blockType, setBlockType] = useState('ip');
  const [blockValue, setBlockValue] = useState('');
  const [blockReason, setBlockReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, b] = await Promise.all([
        supabaseGet('fraud_scores?order=created_at.desc&limit=100'),
        supabaseGet('fraud_rules?order=priority.asc'),
        supabaseGet('fraud_blocklist?order=created_at.desc&limit=100'),
      ]);
      setScores(s || []);
      setRules(r || []);
      setBlocklist(b || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRule = async (rule: FraudRule) => {
    await supabasePatch(`fraud_rules?id=eq.${rule.id}`, { is_active: !rule.is_active }).catch(() => {});
    setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
  };

  const addToBlocklist = async () => {
    if (!blockValue) return;
    try {
      await supabasePost('fraud_blocklist', { block_type: blockType, block_value: blockValue, reason: blockReason || null });
      setBlockValue('');
      setBlockReason('');
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const removeFromBlocklist = async (id: string) => {
    await supabaseDelete(`fraud_blocklist?id=eq.${id}`).catch(() => {});
    setBlocklist(prev => prev.filter(b => b.id !== id));
  };

  const markReviewed = async (id: string) => {
    await supabasePatch(`fraud_scores?id=eq.${id}`, { reviewed: true }).catch(() => {});
    setScores(prev => prev.map(s => s.id === id ? { ...s, reviewed: true } : s));
  };

  // Stats
  const totalFlagged = scores.filter(s => s.risk_level !== 'low').length;
  const highRisk = scores.filter(s => s.risk_level === 'high' || s.risk_level === 'blocked').length;
  const blocked = scores.filter(s => s.action_taken === 'block').length;
  const avgScore = scores.length > 0 ? Math.round(scores.reduce((s, f) => s + f.fraud_score, 0) / scores.length) : 0;

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ShieldAlert className="h-6 w-6 text-red-400" />
              Fraud Detection
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Real-time fraud scoring, blocklist management, and configurable detection rules.
            </p>
          </div>
          <Button variant="outline" onClick={load} className="text-slate-400">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{scores.length}</div>
              <div className="text-xs text-slate-400">Sessions Scored</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-400">{totalFlagged}</div>
              <div className="text-xs text-slate-400">Flagged</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-400">{highRisk}</div>
              <div className="text-xs text-slate-400">High Risk</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-slate-300">{avgScore}</div>
              <div className="text-xs text-slate-400">Avg Fraud Score</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {([['dashboard', 'Flagged Sessions'], ['rules', 'Detection Rules'], ['blocklist', 'Blocklist']] as [ActiveTab, string][]).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? 'bg-red-500/20 text-red-400 border border-red-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-red-400 animate-spin" /></div>
        ) : (
          <>
            {/* Dashboard */}
            {tab === 'dashboard' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-700">
                          <th className="text-left py-3 px-4">Session</th>
                          <th className="text-center py-3 px-2">Score</th>
                          <th className="text-center py-3 px-2">Risk</th>
                          <th className="text-left py-3 px-2">IP</th>
                          <th className="text-left py-3 px-2">Signals</th>
                          <th className="text-center py-3 px-2">Action</th>
                          <th className="text-right py-3 px-4">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scores.filter(s => s.risk_level !== 'low').length === 0 ? (
                          <tr><td colSpan={7} className="py-8 text-center text-slate-500">No flagged sessions. Clean traffic.</td></tr>
                        ) : scores.filter(s => s.risk_level !== 'low').map(s => (
                          <tr key={s.id} className={`border-b border-slate-800 hover:bg-slate-800/50 ${s.reviewed ? 'opacity-50' : ''}`}>
                            <td className="py-2 px-4 font-mono text-xs text-slate-400">{s.session_id.slice(0, 12)}...</td>
                            <td className="py-2 px-2 text-center">
                              <span className={`font-bold ${s.fraud_score >= 70 ? 'text-red-400' : s.fraud_score >= 40 ? 'text-amber-400' : 'text-slate-400'}`}>
                                {s.fraud_score}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[s.risk_level]}`}>{s.risk_level}</span>
                            </td>
                            <td className="py-2 px-2 text-xs text-slate-400">{s.ip_address || '—'}</td>
                            <td className="py-2 px-2 text-xs text-slate-500 max-w-[200px] truncate">
                              {Object.keys(s.signals || {}).join(', ') || '—'}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {!s.reviewed && (
                                <button onClick={() => markReviewed(s.id)} className="text-xs text-slate-500 hover:text-emerald-400">
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </td>
                            <td className="py-2 px-4 text-right text-xs text-slate-500">
                              {new Date(s.created_at).toLocaleTimeString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rules */}
            {tab === 'rules' && (
              <div className="space-y-2">
                {rules.map(rule => (
                  <Card key={rule.id} className={`border ${rule.is_active ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-50'}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <button onClick={() => toggleRule(rule)} className="shrink-0">
                        {rule.is_active ? <ToggleRight className="h-6 w-6 text-red-400" /> : <ToggleLeft className="h-6 w-6 text-slate-600" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white">{rule.rule_name.replace(/_/g, ' ')}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{rule.description}</div>
                      </div>
                      <span className={`w-12 text-center font-bold text-sm ${rule.points >= 30 ? 'text-red-400' : rule.points >= 15 ? 'text-amber-400' : 'text-slate-400'}`}>
                        +{rule.points}
                      </span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Blocklist */}
            {tab === 'blocklist' && (
              <div className="space-y-4">
                <Card className="bg-slate-800/50 border-red-500/30">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-sm font-medium text-white">Add to Blocklist</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Type</label>
                        <select value={blockType} onChange={e => setBlockType(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                          <option value="ip">IP Address</option>
                          <option value="ip_range">IP Range (CIDR)</option>
                          <option value="email">Email</option>
                          <option value="fingerprint">Fingerprint</option>
                          <option value="user_agent">User Agent</option>
                          <option value="country">Country</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Value</label>
                        <input value={blockValue} onChange={e => setBlockValue(e.target.value)} placeholder={blockType === 'ip' ? '1.2.3.4' : blockType === 'email' ? 'spam@example.com' : '...'} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Reason</label>
                        <input value={blockReason} onChange={e => setBlockReason(e.target.value)} placeholder="Spam bot" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                      </div>
                    </div>
                    <Button onClick={addToBlocklist} disabled={!blockValue} className="bg-red-600 hover:bg-red-700">
                      <Ban className="h-4 w-4 mr-1" /> Block
                    </Button>
                  </CardContent>
                </Card>

                <div className="space-y-2">
                  {blocklist.length === 0 ? (
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-6 text-center text-sm text-slate-500">Blocklist is empty</CardContent>
                    </Card>
                  ) : blocklist.map(b => (
                    <Card key={b.id} className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-3 flex items-center gap-3">
                        <Ban className="h-4 w-4 text-red-400 shrink-0" />
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400">{b.block_type}</span>
                        <span className="text-sm text-white font-mono flex-1">{b.block_value}</span>
                        <span className="text-xs text-slate-500">{b.reason}</span>
                        <button onClick={() => removeFromBlocklist(b.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PortalLayout>
  );
}
